"use client";

import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/utils/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Quote, CheckCircle, ArrowRight, Eye, Download } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

const ClientOverview: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();

  const enabled = !!profile?.id && profile.role === "client";

  const { data: unpaidInvoices, isLoading: invLoading, error: invError } = useQuery({
    queryKey: ["client-unpaid-invoices", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      const { data, error } = await supabase
        .from("invoices")
        .select("id, invoice_number, total_amount, status, due_date, signature_url, signed_at")
        .eq("client_id", profile.id)
        .eq("status", "sent")
        .order("due_date", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled,
  });

  const { data: recentQuotes, isLoading: quotesLoading, error: quotesError } = useQuery({
    queryKey: ["client-recent-quotes", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      const { data, error } = await supabase
        .from("quotes")
        .select("id, quote_number, total_amount, status, valid_until, signature_url, signed_at, created_at")
        .eq("client_id", profile.id)
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data || [];
    },
    enabled,
  });

  const { data: signedDocs, isLoading: signedLoading, error: signedError } = useQuery({
    queryKey: ["client-signed-docs-overview", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      const [invoicesRes, quotesRes] = await Promise.all([
        supabase
          .from("invoices")
          .select("id, invoice_number, total_amount, signed_at, signature_url")
          .eq("client_id", profile.id)
          .not("signature_url", "is", null)
          .order("signed_at", { ascending: false })
          .limit(5),
        supabase
          .from("quotes")
          .select("id, quote_number, total_amount, signed_at, signature_url")
          .eq("client_id", profile.id)
          .not("signature_url", "is", null)
          .order("signed_at", { ascending: false })
          .limit(5),
      ]);

      const invs = (invoicesRes.data || []).map((i: any) => ({
        id: i.id,
        type: "invoice" as const,
        number: i.invoice_number,
        total_amount: i.total_amount,
        signed_at: i.signed_at,
        signature_url: i.signature_url,
      }));

      const qts = (quotesRes.data || []).map((q: any) => ({
        id: q.id,
        type: "quote" as const,
        number: q.quote_number,
        total_amount: q.total_amount,
        signed_at: q.signed_at,
        signature_url: q.signature_url,
      }));

      return [...invs, ...qts];
    },
    enabled,
  });

  if (!profile) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Client Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              Please log in to view your overview.
            </AlertDescription>
          </Alert>
          <Button className="mt-4" onClick={() => navigate("/login")}>
            Go to Login
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (profile.role !== "client") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Client Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              This section is tailored for client users. Switch to a client account to view it.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Unpaid Invoices */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Unpaid Invoices
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-1/3" />
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : invError ? (
            <Alert variant="destructive">
              <AlertDescription>Failed to load invoices.</AlertDescription>
            </Alert>
          ) : unpaidInvoices && unpaidInvoices.length > 0 ? (
            <div className="space-y-2">
              {unpaidInvoices.slice(0, 5).map((inv: any) => (
                <div key={inv.id} className="flex items-center justify-between p-3 border rounded">
                  <div>
                    <div className="font-medium">Invoice #{inv.invoice_number}</div>
                    <p className="text-sm text-muted-foreground">
                      Due {inv.due_date ? format(new Date(inv.due_date), "MMM dd, yyyy") : "N/A"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">£{inv.total_amount?.toFixed(2)}</Badge>
                    {inv.signature_url ? (
                      <Badge variant="outline">Signed</Badge>
                    ) : (
                      <Badge variant="destructive">Awaiting Signature</Badge>
                    )}
                    {inv.signature_url ? (
                      <Button variant="outline" size="sm" asChild>
                        <a href={inv.signature_url} target="_blank" rel="noopener noreferrer">
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </a>
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => navigate("/client-portal/signed")}>
                        Review
                        <Eye className="ml-1 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No unpaid invoices.</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Quotes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Quote className="h-5 w-5" />
            Recent Quotes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {quotesLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-1/3" />
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : quotesError ? (
            <Alert variant="destructive">
              <AlertDescription>Failed to load quotes.</AlertDescription>
            </Alert>
          ) : recentQuotes && recentQuotes.length > 0 ? (
            <div className="space-y-2">
              {recentQuotes.map((q: any) => (
                <div key={q.id} className="flex items-center justify-between p-3 border rounded">
                  <div>
                    <div className="font-medium">Quote #{q.quote_number}</div>
                    <p className="text-sm text-muted-foreground">
                      {q.valid_until ? `Valid until ${format(new Date(q.valid_until), "MMM dd, yyyy")}` : "No expiry"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">£{q.total_amount?.toFixed(2)}</Badge>
                    <Badge variant={q.status === "accepted" ? "default" : "secondary"}>{q.status}</Badge>
                    {q.signature_url ? (
                      <Button variant="outline" size="sm" asChild>
                        <a href={q.signature_url} target="_blank" rel="noopener noreferrer">
                          <Download className="h-4 w-4 mr-1" />
                          Signed
                        </a>
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => navigate("/client-portal/signed")}>
                        Review
                        <Eye className="ml-1 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No quotes yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Signed Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Recently Signed
          </CardTitle>
        </CardHeader>
        <CardContent>
          {signedLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-1/3" />
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : signedError ? (
            <Alert variant="destructive">
              <AlertDescription>Failed to load signed documents.</AlertDescription>
            </Alert>
          ) : signedDocs && signedDocs.length > 0 ? (
            <div className="space-y-2">
              {signedDocs.map((doc: any) => (
                <div key={doc.id} className="flex items-center justify-between p-3 border rounded">
                  <div>
                    <div className="font-medium">
                      {doc.type === "invoice" ? "Invoice" : "Quote"} #{doc.number}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Signed {doc.signed_at ? format(new Date(doc.signed_at), "MMM dd, yyyy HH:mm") : "N/A"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">£{doc.total_amount?.toFixed(2)}</Badge>
                    <Button variant="outline" size="sm" asChild>
                      <a href={doc.signature_url} target="_blank" rel="noopener noreferrer">
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </a>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No signed documents yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientOverview;